import * as React from 'react';
import { UserContext } from '../context/userContext';

import Box from '@mui/material/Box';

import { Helmet } from 'react-helmet';
import { Paper, Stack, Typography } from '@mui/material';
import { ActionBar } from '../molecules';

export const HintsPage: React.FC = () => {
  const { loggedIn } = React.useContext(UserContext);

  return loggedIn ? (
    <>
      <Helmet>
        <title>Hints page</title>
      </Helmet>
      <ActionBar title="User Instructions for Industrial Knowledge Manager (IKM)" />
      <Box component="main">
        <Box component="main" p={2}>
          <Paper sx={{ padding: 2 }}>
            <Stack direction="row" spacing={2}>
              <Box>
                <Typography variant="h6">
                  Understanding the Difference:
                </Typography>
                <ul>
                  <li>
                    <strong>Not a General Search Engine:</strong> Unlike Google
                    or general chatbots (e.g., ChatGPT), adding just one or two
                    keywords will not return search results which mention the
                    term(s). Instead, the IKM attempts to understand the context
                    or meaning of you query. Based on this, it retrieves and
                    provides a select handful of highly relevant document
                    excerpts based on the precise context of your query and
                    generates a response based on these excerpts.
                  </li>
                </ul>
                <Typography variant="h6">Purpose and Scope of IKM:</Typography>
                <ul>
                  <li>
                    <strong>Designed for Industry Professionals:</strong> The
                    IKM is specifically built to capture, validate, and deliver
                    precise knowledge tailored for Energy & Chemical technical
                    professionals. It integrates a wide variety of
                    industry-specific technical documents, including public
                    documents such as regulations and standards, as well as
                    internal company best practices and procedures.
                  </li>
                </ul>
                <Typography variant="h6">How to Formulate Queries:</Typography>
                <ul>
                  <li>
                    <strong>Be Explicit and Precise:</strong>Clearly and
                    specifically state the information or action you're seeking.
                    Ambiguity leads to unclear answers or irrelevant document
                    retrieval.
                    <ul>
                      <li>
                        <strong>Example of a well-formulated question:</strong>
                      </li>
                      <ul>
                        <li>
                          Correct: "Provide a one-paragraph summary of the
                          previous answer."
                        </li>
                        <li>
                          Incorrect: "Summarize this" or "Provide one paragraph
                          summary."
                        </li>
                      </ul>
                    </ul>
                  </li>
                  <li>
                    <strong>Provide Detailed Context:</strong> If you need
                    specific examples, state your request explicitly, e.g.,
                    <ul>
                      <li>
                        Correct: "List three examples of Management of Change
                        (MOC) processes related to valve replacements."
                      </li>
                      <li>Incorrect: "Examples of MOC processes."</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Verbose Queries Improve Accuracy:</strong> Unlike a
                    search engine, the IKM relies on the context provided in
                    your query. The more relevant context and detail you
                    provide, the better the system can retrieve and rank
                    relevant mentions. Conversely, irrelevant information can
                    degrade the quality of responses.
                  </li>
                </ul>
              </Box>
              <Box>
                <Typography variant="h6">
                  Engage with Subject Matter Experts (SMEs):
                </Typography>
                <ul>
                  <li>
                    <strong>Engaging SMEs:</strong>The IKM helps you efficiently
                    identify and contact Subject Matter Experts within your
                    organization, promoting better collaboration and
                    facilitating direct knowledge sharing.
                  </li>
                </ul>
                <Typography variant="h6">Organizing Conversations:</Typography>
                <ul>
                  <li>
                    <strong>
                      Use Separate Conversations for Different Topics:
                    </strong>
                    For optimal performance and clarity, it's best to keep one
                    topic or closely related set of questions per conversation.
                    When switching to a completely different topic, start a new
                    conversation. This helps the system enhance its
                    understanding of your intent so it can retrieve the most
                    relevant and helpful information.
                  </li>
                </ul>

                <p>
                  Following these guidelines will ensure you get precise,
                  contextually relevant, and useful responses from the
                  Industrial Knowledge Manager, maximizing your productivity and
                  reducing unnecessary effort.
                </p>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </>
  ) : null;
};
